import React, { useEffect, useState } from 'react';
import { fetchPaymentsNeedingReconciliation, resolveWorldpayReconciliationFlag, CancellationPayment } from '../api/payments';

const currencyFormatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

const VIA_LABELS: Record<string, string> = {
  staff: 'staff-confirmed',
  webhook: 'Worldpay webhook',
  unknown: 'unknown channel',
};

const viaLabel = (via?: string): string => (via && VIA_LABELS[via]) || via || 'unknown channel';

/**
 * Read-only surface for `GET /ui/admin/cancellation-payments/needs-
 * reconciliation` (final fix-wave C2) -- payments where money was recorded
 * but the settlement would have overpaid the record (or, pre-existing, an
 * ambiguous Worldpay ledger match), so it was deliberately left short of
 * auto-completing for a human to resolve. Parallel to WorldpayUnmatchedQueue
 * (self-contained, fetches on mount, and keeps a request failure visible
 * with a retry control so it cannot be mistaken for an empty queue).
 */
export const WorldpayNeedsReconciliationQueue: React.FC = () => {
  const [payments, setPayments] = useState<CancellationPayment[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [queueLoadError, setQueueLoadError] = useState(false);

  const load = () => {
    fetchPaymentsNeedingReconciliation()
      .then((data) => {
        setPayments(data || []);
        setQueueLoadError(false);
      })
      .catch(() => {
        setPayments([]);
        setQueueLoadError(true);
      })
      .finally(() => setLoaded(true));
  };

  useEffect(() => { load(); }, []);

  // The other half of this queue (the bug fix this branch adds): a human
  // reviews the entry and confirms it's fine. Matches the window.prompt
  // pattern HierarchicalClaimsTable already uses for a required free-text
  // reason. On success the item is simply dropped from local state --
  // it's already gone from the backend's needs-reconciliation query, so
  // there's nothing to re-fetch for.
  const handleResolve = async (payment: CancellationPayment): Promise<void> => {
    const paymentId = payment.id;
    const note = window.prompt('Note (required) -- what did you check, and why is this fine to resolve?', '');
    if (note === null) return; // cancelled
    const trimmed = note.trim();
    if (!trimmed) {
      setResolveError('A note is required to resolve this flag.');
      return;
    }
    setResolveError(null);
    setResolvingId(paymentId);
    try {
      await resolveWorldpayReconciliationFlag(paymentId, {
        notes: trimmed,
        expectedDetectedAt: payment.reconciliationDetectedAt || '',
      });
      setPayments((prev) => prev.filter((p) => p.id !== paymentId));
    } catch {
      setResolveError('Could not resolve this flag -- please try again.');
    } finally {
      setResolvingId(null);
    }
  };

  if (!loaded || (payments.length === 0 && !queueLoadError)) return null;

  return (
    <div className="admin-payments-feedback warning" role="region" aria-label="Payments needing reconciliation">
      {queueLoadError ? (
        <div className="admin-payments-feedback error" role="alert">
          The reconciliation queue is unavailable right now — retry to check for incidents.
        </div>
      ) : (
        <strong>
          {payments.length} payment{payments.length === 1 ? '' : 's'} need{payments.length === 1 ? 's' : ''} reconciliation
        </strong>
      )}
      <button type="button" onClick={load} style={{ marginLeft: '12px' }}>
        {queueLoadError ? 'Retry' : 'Refresh'}
      </button>
      {resolveError && <div className="admin-payments-feedback error" role="alert">{resolveError}</div>}
      <ul>
        {payments.map((payment) => {
          const discrepancy = payment.worldpayAmountDiscrepancy;
          if (discrepancy) {
            // A keying slip: Worldpay actually charged (or refunded) a
            // DIFFERENT figure than what was recorded -- real money moved
            // wrongly, not merely an ambiguous match. Rendered inside its
            // own red/error-styled block, with a badge, so it can never be
            // mistaken for the routine amber overpayment/ambiguous-match
            // line below.
            const diff = discrepancy.reportedAmount - discrepancy.recordedAmount;
            const diffDirection = diff > 0 ? 'more than' : diff < 0 ? 'less than' : 'the same as';
            return (
              <li key={payment.id} className="reconciliation-discrepancy-item">
                <div
                  className={`admin-payments-feedback error reconciliation-discrepancy${discrepancy.severe ? ' severe' : ''}`}
                  role="alert"
                >
                  <strong>{payment.clientName || payment.clientEmail}</strong>
                  <span className={discrepancy.severe ? 'discrepancy-severe-badge' : 'discrepancy-badge'}>
                    {discrepancy.severe ? 'SEVERE AMOUNT MISMATCH' : 'AMOUNT MISMATCH'}
                  </span>
                  <div className="reconciliation-discrepancy-figures">
                    Recorded <strong>{currencyFormatter.format(discrepancy.recordedAmount)}</strong>{' '}
                    ({viaLabel(discrepancy.recordedVia)}) vs. reported{' '}
                    <strong>{currencyFormatter.format(discrepancy.reportedAmount)}</strong>{' '}
                    ({viaLabel(discrepancy.reportedVia)}) — {currencyFormatter.format(Math.abs(diff))}{' '}
                    {diffDirection} what was recorded
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleResolve(payment)}
                  disabled={resolvingId === payment.id}
                  style={{ marginTop: '4px' }}
                >
                  {resolvingId === payment.id ? 'Resolving…' : 'Resolve'}
                </button>
              </li>
            );
          }
          return (
            <li key={payment.id}>
              <strong>{payment.clientName || payment.clientEmail}</strong> — took{' '}
              {currencyFormatter.format(payment.manualPayment?.amountPaid ?? payment.amount ?? 0)}{' '}
              against an expected{' '}
              {currencyFormatter.format(payment.manualPayment?.expectedAmount ?? payment.amount ?? 0)} —{' '}
              {payment.reconciliationReason || 'flagged for manual reconciliation'}{' '}
              <button
                type="button"
                onClick={() => handleResolve(payment)}
                disabled={resolvingId === payment.id}
              >
                {resolvingId === payment.id ? 'Resolving…' : 'Resolve'}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default WorldpayNeedsReconciliationQueue;
